import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Calculator, PiggyBank, Car, FileText, Plus, Trash2, Loader2, Download,
  AlertTriangle, Sparkles, Check, Upload, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { accountingApi, projectApi, expenseApi } from '../lib/api';
import BulkReceiptImport from '../components/BulkReceiptImport';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const currentYear = new Date().getFullYear();
const YEARS = [currentYear, currentYear - 1, currentYear - 2];
const SCHEDULE_C_CATEGORIES = [
  'Car & Truck', 'Legal & Professional', 'Materials & Supplies',
  'Contract Labor', 'Tools & Equipment Rentals', 'Other Business Expenses',
];

export default function Accounting() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(currentYear);
  const [summary, setSummary] = useState(null);
  const [report1099, setReport1099] = useState(null);
  const [mileage, setMileage] = useState([]);
  const [bizExpenses, setBizExpenses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [setasidePct, setSetasidePct] = useState(30);

  const loadStatus = useCallback(async () => {
    try {
      const res = await accountingApi.getStatus();
      setStatus(res.data);
      setSetasidePct(res.data.setaside_pct ?? 30);
    } catch { /* noop */ } finally { setLoading(false); }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [s, r, m, p] = await Promise.all([
        accountingApi.getSummary(year),
        accountingApi.get1099Report(year),
        accountingApi.getMileage(year),
        projectApi.getAll(),
      ]);
      setSummary(s.data);
      setReport1099(r.data);
      setMileage(m.data);
      setProjects(p.data);
      const be = await accountingApi.getBusinessExpenses(year);
      setBizExpenses(be.data);
    } catch { toast.error('Failed to load accounting data'); }
  }, [year]);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => { if (status?.enabled) loadData(); }, [status?.enabled, loadData]);

  const handleUnlock = async () => {
    try {
      await accountingApi.enable(true);
      toast.success('Solo Builder Accounting unlocked!');
      loadStatus();
    } catch { toast.error('Failed to enable add-on'); }
  };

  const saveSetaside = async (pct) => {
    setSetasidePct(pct);
    try {
      await accountingApi.updateSettings(pct);
      loadData();
    } catch { toast.error('Failed to update set-aside %'); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  if (!status?.enabled) {
    return <UpgradeCard canManage={status?.can_manage} onUnlock={handleUnlock} />;
  }

  return (
    <div className="space-y-6" data-testid="accounting-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calculator className="h-6 w-6 text-orange-500" /> Tax & Solo Accounting
          </h1>
          <p className="text-sm text-muted-foreground">Schedule C prep, self-employment tax estimates, mileage & 1099 tracking.</p>
        </div>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-32 rounded-sm" data-testid="accounting-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {summary && (
        <>
          {/* Top KPI row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Kpi label="Gross Income (collected)" value={fmt(summary.gross_income)} icon={TrendingUp} tint="text-green-600" testid="kpi-gross" />
            <Kpi label="Total Deductions" value={fmt(summary.total_expenses)} icon={FileText} tint="text-blue-600" testid="kpi-expenses" />
            <Kpi label="Net Profit (est.)" value={fmt(summary.net_profit)} icon={Calculator} tint="text-orange-600" testid="kpi-net" />
          </div>

          {/* Tax set-aside + mileage vs actual */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Quarterly Tax Jar */}
            <Card className="rounded-sm border-orange-200 dark:border-orange-900" data-testid="tax-jar-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <PiggyBank className="h-4 w-4 text-orange-500" /> Quarterly Tax Savings Jar
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted-foreground">Recommended set-aside ({setasidePct}%)</span>
                  <span className="text-2xl font-bold text-orange-600 tabular-nums" data-testid="setaside-total">{fmt(summary.recommended_setaside)}</span>
                </div>
                <div className="flex items-baseline justify-between border-t pt-2">
                  <span className="text-sm text-muted-foreground">Set aside each quarter</span>
                  <span className="text-lg font-semibold tabular-nums" data-testid="setaside-quarterly">{fmt(summary.recommended_quarterly)}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>Est. self-employment tax</span>
                  <span className="tabular-nums">{fmt(summary.self_employment_tax)}</span>
                </div>
                <div className="pt-2">
                  <Label className="text-xs">Adjust set-aside %: {setasidePct}%</Label>
                  <input
                    type="range" min="15" max="45" step="1" value={setasidePct}
                    onChange={(e) => setSetasidePct(Number(e.target.value))}
                    onMouseUp={(e) => saveSetaside(Number(e.target.value))}
                    onTouchEnd={(e) => saveSetaside(Number(e.target.value))}
                    className="w-full accent-orange-500" data-testid="setaside-slider"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Mileage vs Actual */}
            <Card className="rounded-sm" data-testid="mileage-compare-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Car className="h-4 w-4 text-blue-500" /> Vehicle Write-off: Mileage vs Actual
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <CompareRow label={`Standard mileage (${summary.mileage_miles} mi)`} value={summary.mileage_deduction}
                  best={summary.mileage_vs_actual.better_method === 'mileage'} />
                <CompareRow label="Actual vehicle expenses logged" value={summary.vehicle_actual_expenses}
                  best={summary.mileage_vs_actual.better_method === 'actual'} />
                {summary.vehicle_actual_expenses > 0 && (
                  <div className="flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 rounded-sm p-2.5 mt-2">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>If you take the <strong>Standard Mileage</strong> deduction, don't also deduct individual fuel receipts — that's double dipping.</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Schedule C breakdown */}
          <Card className="rounded-sm" data-testid="schedule-c-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Schedule C Expenses — {year}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                {Object.entries(summary.expenses_by_schedule_c).map(([cat, amt]) => (
                  <div key={cat} className="flex items-center justify-between text-sm border-b py-1.5">
                    <span className="text-muted-foreground">{cat}</span>
                    <span className="font-medium tabular-nums">{fmt(amt)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* 1099 Dashboard */}
          <Report1099 report={report1099} year={year} onReload={loadData} />

          {/* Business (overhead) expenses */}
          <BusinessExpenses expenses={bizExpenses} projects={projects} onReload={loadData} />

          {/* Mileage log */}
          <MileageSection mileage={mileage} projects={projects} onReload={loadData} />

          <p className="text-xs text-muted-foreground text-center pb-4">
            {summary.disclaimer}
          </p>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, tint, testid }) {
  return (
    <Card className="rounded-sm" data-testid={testid}>
      <CardContent className="pt-5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
          <Icon className={`h-4 w-4 ${tint}`} /> {label}
        </div>
        <p className={`text-2xl font-bold tabular-nums ${tint}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

function CompareRow({ label, value, best }) {
  return (
    <div className={`flex items-center justify-between rounded-sm p-2.5 ${best ? 'bg-green-50 dark:bg-green-950/40 border border-green-200 dark:border-green-900' : 'bg-muted/40'}`}>
      <span className="text-sm flex items-center gap-2">
        {label}
        {best && <Badge className="bg-green-600 hover:bg-green-600 text-xs">Bigger write-off</Badge>}
      </span>
      <span className="font-semibold tabular-nums">{fmt(value)}</span>
    </div>
  );
}

function UpgradeCard({ canManage, onUnlock }) {
  return (
    <div className="max-w-2xl mx-auto py-10" data-testid="accounting-upgrade-card">
      <Card className="rounded-sm border-orange-200 dark:border-orange-900 overflow-hidden">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 p-6 text-white">
          <Sparkles className="h-8 w-8 mb-2" />
          <h2 className="text-2xl font-bold">Solo Builder Accounting</h2>
          <p className="text-orange-50 mt-1">Tax-time superpowers for the one-person crew.</p>
        </div>
        <CardContent className="pt-6 space-y-3">
          {[
            'Self-employment tax estimates + a quarterly "tax jar" target',
            'Schedule C expense categorization for painless filing',
            'Standard mileage log with auto IRS rates (and vs. actual comparison)',
            'Subcontractor 1099 tracking with $600 threshold alerts + CSV export',
          ].map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-green-600 shrink-0 mt-0.5" /> {f}
            </div>
          ))}
          {canManage ? (
            <Button onClick={onUnlock} className="w-full mt-4 rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="unlock-accounting-btn">
              <Sparkles className="h-4 w-4 mr-2" /> Unlock Solo Accounting
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground mt-4 text-center">Ask your account owner to enable this add-on.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Report1099({ report, year, onReload }) {
  const [editing, setEditing] = useState(null); // sub id being edited
  const [taxId, setTaxId] = useState('');
  const [w9, setW9] = useState(false);
  const fileRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);

  if (!report) return null;

  const startEdit = (s) => { setEditing(s.id); setTaxId(''); setW9(s.w9_on_file); };
  const saveTax = async (subId) => {
    try {
      await accountingApi.updateSubTax(subId, { tax_id: taxId, w9_on_file: w9 });
      toast.success('Saved');
      setEditing(null);
      onReload();
    } catch { toast.error('Failed to save'); }
  };
  const uploadW9 = async (subId, file) => {
    if (!file) return;
    setUploadingId(subId);
    try {
      await accountingApi.uploadW9(subId, file);
      toast.success('W-9 uploaded');
      onReload();
    } catch { toast.error('Upload failed'); } finally { setUploadingId(null); }
  };
  const downloadCsv = async () => {
    try {
      const res = await accountingApi.download1099Csv(year);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `1099-report-${year}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <Card className="rounded-sm" data-testid="report-1099-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> 1099 Tracker — {year}
            {report.count_requiring_1099 > 0 && (
              <Badge className="bg-red-600 hover:bg-red-600">{report.count_requiring_1099} need a 1099</Badge>
            )}
          </span>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={downloadCsv} data-testid="export-1099-csv-btn">
            <Download className="h-4 w-4 mr-1.5" /> Export 1099 CSV
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {report.subcontractors.length === 0 ? (
          <p className="text-center text-muted-foreground py-4 text-sm">No subcontractors yet.</p>
        ) : report.subcontractors.map((s) => (
          <div key={s.id} className="border rounded-sm p-3" data-testid={`sub-1099-${s.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium text-sm flex items-center gap-2">
                  {s.name}
                  {s.requires_1099 && <Badge className="bg-red-600 hover:bg-red-600 text-xs">1099 Required</Badge>}
                </p>
                <p className="text-xs text-muted-foreground">
                  Paid {fmt(s.total_paid_ytd)} · Tax ID {s.tax_id_on_file ? '✓' : '—'} · W-9 {s.w9_on_file ? '✓' : '—'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden"
                  onChange={(e) => uploadW9(s.id, e.target.files?.[0])} />
                <Button size="sm" variant="ghost" className="h-7 text-xs" disabled={uploadingId === s.id}
                  onClick={() => { fileRef.current.onchange = (e) => uploadW9(s.id, e.target.files?.[0]); fileRef.current.click(); }}
                  data-testid={`upload-w9-${s.id}`}>
                  {uploadingId === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />} W-9
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs rounded-sm" onClick={() => startEdit(s)}
                  data-testid={`edit-sub-tax-${s.id}`}>Tax Info</Button>
              </div>
            </div>
            {/* Progress toward $600 threshold */}
            <div className="mt-2">
              <Progress value={Math.min(100, (s.total_paid_ytd / 600) * 100)} className="h-1.5" />
              <p className="text-[11px] text-muted-foreground mt-1">{fmt(s.total_paid_ytd)} / $600 threshold</p>
            </div>
            {editing === s.id && (
              <div className="mt-3 flex items-end gap-2 flex-wrap border-t pt-3">
                <div className="flex-1 min-w-[160px]">
                  <Label className="text-xs">EIN / SSN</Label>
                  <Input value={taxId} onChange={(e) => setTaxId(e.target.value)} placeholder="12-3456789"
                    className="h-8 text-sm rounded-sm mt-1" data-testid={`tax-id-input-${s.id}`} />
                </div>
                <label className="flex items-center gap-1.5 text-sm h-8">
                  <input type="checkbox" checked={w9} onChange={(e) => setW9(e.target.checked)} className="accent-orange-500" />
                  W-9 on file
                </label>
                <Button size="sm" className="h-8 rounded-sm bg-orange-500 hover:bg-orange-600" onClick={() => saveTax(s.id)}
                  data-testid={`save-sub-tax-${s.id}`}>Save</Button>
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditing(null)}>Cancel</Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function BusinessExpenses({ expenses, projects = [], onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split('T')[0], amount: '', description: '',
    schedule_c_category: 'Other Business Expenses', vendor: '', notes: '',
  });
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);
  const scanRef = useRef(null);

  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);

  const handleScan = async (file) => {
    if (!file) return;
    setScanning(true);
    setReceipt(file);
    try {
      const res = await expenseApi.scanReceipt(file);
      const d = res.data || {};
      setForm((f) => ({
        ...f,
        description: d.description || d.vendor || f.description,
        amount: d.total ? String(d.total) : f.amount,
        vendor: d.vendor || f.vendor,
        date: d.date || f.date,
        schedule_c_category: d.schedule_c_category || f.schedule_c_category,
      }));
      toast.success('Receipt scanned — review the details and save');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the receipt. Enter details manually.');
    } finally {
      setScanning(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Description and amount are required');
      return;
    }
    setSaving(true);
    try {
      await accountingApi.addBusinessExpense({ ...form, amount: parseFloat(form.amount), receipt });
      toast.success('Business expense added');
      setForm({ date: new Date().toISOString().split('T')[0], amount: '', description: '', schedule_c_category: 'Other Business Expenses', vendor: '', notes: '' });
      setReceipt(null);
      setOpen(false);
      onReload();
    } catch { toast.error('Failed to add expense'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await accountingApi.deleteBusinessExpense(id); onReload(); } catch { toast.error('Failed'); }
  };

  return (
    <Card className="rounded-sm" data-testid="business-expenses-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> Business Expenses <span className="text-sm font-normal text-muted-foreground">(overhead — no job)</span>
            <span className="text-sm font-normal text-muted-foreground">· {fmt(total)}</span>
          </span>
          <div className="flex items-center gap-2">
            <BulkReceiptImport projects={projects} onDone={onReload} />
            <Button size="sm" className="rounded-sm bg-orange-500 hover:bg-orange-600" onClick={() => setOpen(true)} data-testid="add-business-expense-btn">
              <Plus className="h-4 w-4 mr-1.5" /> Add Business Expense
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {expenses.length === 0 ? (
          <p className="text-center text-muted-foreground py-4 text-sm">
            No overhead expenses yet. Add insurance, phone, software, fuel, licenses, etc.
          </p>
        ) : (
          <div className="divide-y">
            {expenses.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-2 group text-sm" data-testid={`biz-expense-row-${e.id}`}>
                <div className="min-w-0">
                  <p className="font-medium">{e.description}{e.vendor ? ` · ${e.vendor}` : ''}</p>
                  <p className="text-xs text-muted-foreground">{e.date} · {e.schedule_c_category}{e.has_receipt ? ' · 📎 receipt' : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold tabular-nums">{fmt(e.amount)}</span>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive opacity-0 group-hover:opacity-100"
                    onClick={() => del(e.id)} data-testid={`delete-biz-expense-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="business-expense-dialog">
          <DialogHeader>
            <DialogTitle>Add Business Expense</DialogTitle>
            <DialogDescription>Overhead not tied to a specific job. Counts toward your Schedule C totals.</DialogDescription>
          </DialogHeader>
          <div className="mb-1">
            <input ref={scanRef} type="file" accept="image/*,application/pdf,.pdf,.heic,.heif" className="hidden"
              onChange={(e) => { handleScan(e.target.files?.[0] || null); e.target.value = ''; }} />
            <Button type="button" variant="outline" className="w-full rounded-sm border-dashed"
              onClick={() => scanRef.current?.click()} disabled={scanning} data-testid="scan-receipt-btn">
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {scanning ? 'Reading receipt…' : 'Scan Receipt to Auto-Fill'}
            </Button>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. General liability insurance" className="rounded-sm mt-1" data-testid="biz-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Amount</Label>
                <Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00" className="rounded-sm mt-1" data-testid="biz-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="rounded-sm mt-1" data-testid="biz-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Schedule C Category</Label>
              <Select value={form.schedule_c_category} onValueChange={(v) => setForm({ ...form, schedule_c_category: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="biz-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCHEDULE_C_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vendor (optional)</Label>
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="e.g. State Farm" className="rounded-sm mt-1" data-testid="biz-vendor-input" />
            </div>
            <div>
              <Label className="text-xs">Note (optional)</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="rounded-sm mt-1" data-testid="biz-note-input" />
            </div>
            <div>
              <Label className="text-xs">Receipt (optional)</Label>
              <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden"
                onChange={(e) => setReceipt(e.target.files?.[0] || null)} />
              <Button type="button" size="sm" variant="outline" className="rounded-sm mt-1 h-8 text-xs w-full"
                onClick={() => fileRef.current?.click()} data-testid="biz-receipt-btn">
                <Upload className="h-3.5 w-3.5 mr-1" />{receipt ? receipt.name.slice(0, 24) : 'Attach Receipt'}
              </Button>
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="save-biz-expense-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Add Expense
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function MileageSection({ mileage, projects, onReload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: new Date().toISOString().split('T')[0], miles: '', description: '', project_id: '' });
  const [saving, setSaving] = useState(false);

  const total = mileage.reduce((s, m) => s + (m.total_deduction || 0), 0);
  const totalMiles = mileage.reduce((s, m) => s + (m.miles || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.miles || parseFloat(form.miles) <= 0) { toast.error('Enter miles'); return; }
    setSaving(true);
    try {
      await accountingApi.addMileage({
        date: form.date, miles: parseFloat(form.miles),
        description: form.description, project_id: form.project_id || null,
      });
      toast.success('Trip logged');
      setForm({ date: new Date().toISOString().split('T')[0], miles: '', description: '', project_id: '' });
      setOpen(false);
      onReload();
    } catch { toast.error('Failed to log trip'); } finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await accountingApi.deleteMileage(id); onReload(); } catch { toast.error('Failed'); }
  };

  return (
    <Card className="rounded-sm" data-testid="mileage-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
          <span className="flex items-center gap-2">
            <Car className="h-4 w-4" /> Mileage Log
            <span className="text-sm font-normal text-muted-foreground">{totalMiles.toFixed(0)} mi · {fmt(total)}</span>
          </span>
          <Button size="sm" className="rounded-sm bg-orange-500 hover:bg-orange-600" onClick={() => setOpen(true)} data-testid="log-trip-btn">
            <Plus className="h-4 w-4 mr-1.5" /> Log Business Trip
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {mileage.length === 0 ? (
          <p className="text-center text-muted-foreground py-4 text-sm">No trips logged this year.</p>
        ) : (
          <div className="divide-y">
            {mileage.map((m) => (
              <div key={m.id} className="flex items-center justify-between py-2 group text-sm" data-testid={`mileage-row-${m.id}`}>
                <div className="min-w-0">
                  <p className="font-medium">{m.description || 'Business trip'}{m.project_name ? ` · ${m.project_name}` : ''}</p>
                  <p className="text-xs text-muted-foreground">{m.date} · {m.miles} mi @ ${m.rate_used}/mi</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold tabular-nums">{fmt(m.total_deduction)}</span>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive opacity-0 group-hover:opacity-100"
                    onClick={() => del(m.id)} data-testid={`delete-mileage-${m.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="log-trip-dialog">
          <DialogHeader>
            <DialogTitle>Log Business Trip</DialogTitle>
            <DialogDescription>The IRS standard mileage rate is applied automatically by date.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="rounded-sm mt-1" data-testid="trip-date-input" />
            </div>
            <div>
              <Label className="text-xs">Miles</Label>
              <Input type="number" step="0.1" min="0" value={form.miles} onChange={(e) => setForm({ ...form, miles: e.target.value })}
                placeholder="e.g. 42" className="rounded-sm mt-1" data-testid="trip-miles-input" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Supply run to lumber yard" className="rounded-sm mt-1" data-testid="trip-desc-input" />
            </div>
            <div>
              <Label className="text-xs">Project (optional)</Label>
              <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="trip-project-select"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-sm bg-orange-500 hover:bg-orange-600" data-testid="save-trip-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />} Log Trip
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
